/**
 * IMPORTANT, READ THIS BEFORE EDITING THIS FILE:
 *
 * Make sure you update `cip-backend:functions/src/applications/isUserApplicationAutoAcceptEligible.ts`
 * if you make any changes here.
 */

import {useContext, useEffect, useState} from 'react';
import {ProfileContext} from 'contexts/ProfileContext';
import type {CourseId} from 'types/common';
import {useCipHttpsCallable} from '../../../../firebase/functions/use_cip_https_callable';
import {useParams} from 'react-router-dom';
import type {ApplicationType} from 'course/application/firebase/models/applicationData';
import {getParentCourseId} from 'course/application/general/utils/apputils';

////////////////////////////START COPYING HERE//////////////////////////////////
/**
 * The request for the is application auto accepted function.
 */
interface IsUserApplicationAutoAcceptEligibleRequest {
  /**
   * The email of the user.
   */
  userEmail: string;
  /**
   * The ID of the course whose auto accept list we should check.
   */
  courseId: CourseId;
  /**
   * The type of application (e.g. 'student', 'sl')
   */
  applicationType: string;
}

type IsUserApplicationAutoAcceptEligibleResponse = Promise<boolean>;
///////////////////////////////END COPYING HERE/////////////////////////////////

interface UseIsUserApplicationAutoAcceptEligibleResponse {
  isUserApplicationAutoAcceptEligible: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Checks whether the signed-in user is on the target course's auto-accept list
 * for the given application type. The course id is read from the route's
 * `:targetCourseId` param and the email from {@link ProfileContext}; the check is
 * skipped (returning `false`) until both are available.
 *
 * Auto-accept lists only exist for the standard Code in Place course (parent
 * course id `'public'`) — course families like PAI have no such list (and, for
 * a first-time offering, no prior cohort a list could reference), and the
 * student-flow result is never read by any caller. The callable is skipped
 * entirely outside that case, rather than paying for a production Cloud
 * Functions round trip whose answer is always `false`.
 *
 * @param applicationType - Which application to check eligibility for (`student`
 *   or `sl`).
 * @returns The eligibility flag, a combined loading flag (callable warm-up plus
 *   the in-flight request), and any error from either.
 */
export function useApplicationAutoAcceptEligible(
  applicationType: ApplicationType,
): UseIsUserApplicationAutoAcceptEligibleResponse {
  const {targetCourseId: courseId} = useParams();
  const userEmail = useContext(ProfileContext)?.userData?.email;
  const [
    isUserApplicationAutoAcceptEligibleFunction,
    isFunctionLoading,
    isFunctionError,
  ] = useCipHttpsCallable<
    IsUserApplicationAutoAcceptEligibleRequest,
    IsUserApplicationAutoAcceptEligibleResponse
  >('isuserapplicationautoaccepteligible');
  const [
    isUserApplicationAutoAcceptEligible,
    setIsUserApplicationAutoAcceptEligible,
  ] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (
      !userEmail ||
      !courseId ||
      applicationType !== 'sl' ||
      getParentCourseId(courseId) !== 'public'
    ) {
      return;
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setIsLoading(true);
    isUserApplicationAutoAcceptEligibleFunction({
      userEmail,
      courseId,
      applicationType,
    })
      .then(async result => {
        const data = await result.data;
        setIsUserApplicationAutoAcceptEligible(data);
        setIsLoading(false);
      })
      .catch(error => {
        setError(error);
        setIsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [userEmail, courseId, applicationType]);
  return {
    isUserApplicationAutoAcceptEligible,
    isLoading: isFunctionLoading || isLoading,
    error: isFunctionError ?? error,
  };
}
